/** Limiter controls — UI for the limiter/compressor effect.
 *
 * Provides controls for:
 * - Enable/disable toggle
 * - Mode selector (transparent / clipper)
 * - Threshold
 * - Input gain
 * - Output gain
 * - Gain reduction meter
 */

import { ShieldCheck } from "lucide-react";
import { useCallback } from "react";
import type { LimiterParams } from "../types/synth.ts";

/** Props for the LimiterControls component. */
export type LimiterControlsProps = {
	readonly params: LimiterParams;
	readonly onChange: (update: Partial<LimiterParams>) => void;
	readonly enabled: boolean;
};

export function LimiterControls({
	params,
	onChange,
	enabled,
}: LimiterControlsProps) {
	const handleToggle = useCallback(() => {
		onChange({ enabled: !params.enabled });
	}, [params.enabled, onChange]);

	return (
		<div className="limiter-controls">
			<div className="limiter-header">
				<button
					type="button"
					className={`limiter-toggle ${params.enabled ? "active" : ""}`}
					onClick={handleToggle}
					disabled={!enabled}
					title={params.enabled ? "Disable limiter" : "Enable limiter"}
				>
					<ShieldCheck size={12} />
					<span>Limiter</span>
				</button>
			</div>

			{params.enabled && (
				<div className="limiter-body">
					{/* Mode selector */}
					<div className="limiter-row">
						<label className="limiter-label" htmlFor="limiter-mode">
							Mode
						</label>
						<select
							id="limiter-mode"
							className="limiter-select"
							value={params.mode}
							onChange={(e) =>
								onChange({
									mode: e.target.value as LimiterParams["mode"],
								})
							}
							disabled={!enabled}
						>
							<option value="transparent">Transparent</option>
							<option value="clipper">Clipper</option>
						</select>
					</div>

					{/* Threshold */}
					<div className="limiter-row">
						<label className="limiter-label" htmlFor="limiter-threshold">
							Threshold
						</label>
						<input
							id="limiter-threshold"
							type="range"
							className="limiter-slider"
							min={-60}
							max={0}
							step={1}
							value={params.threshold}
							onChange={(e) => onChange({ threshold: Number(e.target.value) })}
							disabled={!enabled}
						/>
						<span className="limiter-value">{params.threshold} dB</span>
					</div>

					{/* Input gain */}
					<div className="limiter-row">
						<label className="limiter-label" htmlFor="limiter-input-gain">
							Input
						</label>
						<input
							id="limiter-input-gain"
							type="range"
							className="limiter-slider"
							min={-24}
							max={24}
							step={0.5}
							value={params.inputGain}
							onChange={(e) => onChange({ inputGain: Number(e.target.value) })}
							disabled={!enabled}
						/>
						<span className="limiter-value">
							{params.inputGain > 0 ? "+" : ""}
							{params.inputGain.toFixed(1)} dB
						</span>
					</div>

					{/* Output gain */}
					<div className="limiter-row">
						<label className="limiter-label" htmlFor="limiter-output-gain">
							Output
						</label>
						<input
							id="limiter-output-gain"
							type="range"
							className="limiter-slider"
							min={-24}
							max={24}
							step={0.5}
							value={params.outputGain}
							onChange={(e) => onChange({ outputGain: Number(e.target.value) })}
							disabled={!enabled}
						/>
						<span className="limiter-value">
							{params.outputGain > 0 ? "+" : ""}
							{params.outputGain.toFixed(1)} dB
						</span>
					</div>
				</div>
			)}
		</div>
	);
}
